import classNames from 'classnames';
import { type ComponentProps, type MouseEvent, memo, type ReactNode, useCallback, useRef, useState } from 'react';

import { type Clip, ClipType } from '@air/api/types';
import { getClipSpritesheetAspectRatio, getClipSpritesheetFrameCount } from '@air/utils-clips';

import { useVideoHlsPreview } from '../hooks/useVideoHlsPreview';
import { useVideoPreview } from '../hooks/useVideoPreview';
import { VideoSeekbarPreview } from './VideoSeekbarPreview';
import { VideoSeekbarPreviewHls } from './VideoSeekbarPreviewHls';
import { VideoSeekbarPreviewSpritesheet } from './VideoSeekbarPreviewSpritesheet';
import { VideoSeekbarProgress } from './VideoSeekbarProgress';
import { VideoSeekbarProgressChapters } from './VideoSeekbarProgressChapters';

export type Chapter = {
  title: string;
  startTime: number;
  endTime: number;
  summary: string;
};

export type VideoSeekbar = Pick<ComponentProps<'div'>, 'className'> & {
  clip: Clip;
  duration: number;
  onSeek: (value: number) => void;
  onSeekEnd: (value: number) => void;
  onSeekStart: () => void;
  processedPercentage: number;
  progressPercentage: number;
  renderTimestamp?: () => ReactNode;
  chapters: Chapter[] | undefined;
  isFullscreen?: boolean;
};

export const VideoSeekbar = memo(
  ({
    className,
    clip,
    duration,
    onSeek,
    onSeekEnd,
    onSeekStart,
    processedPercentage,
    progressPercentage,
    renderTimestamp,
    chapters,
    isFullscreen,
  }: VideoSeekbar) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [seekPosition, setSeekPosition] = useState(0);
    const [position, setPosition] = useState(0);
    const { onResetPreview, onUpdatePreview, onUpdatePreviewTimestamp, videoPreviewRef } = useVideoPreview();
    const { onUpdateVideo, videoRef } = useVideoHlsPreview({ clip });
    const isAudioClip = clip.type === ClipType.audio;

    const onPreviewStart = useCallback(
      (event: MouseEvent<HTMLDivElement>) => {
        if (!inputRef.current || (!clip.assets.spriteSheet?.src && !videoRef)) return null;

        const x = event.clientX - inputRef.current.getBoundingClientRect().left;
        const position = x / inputRef.current.offsetWidth;

        onUpdateVideo({ duration: duration || clip.duration || 0, position });
        setSeekPosition(position);
        onUpdatePreview({ x });
        onUpdatePreviewTimestamp({ duration: duration || clip.duration || 0, position });
        setPosition(position);
      },
      [
        clip.assets.spriteSheet?.src,
        clip.duration,
        duration,
        onUpdatePreview,
        onUpdatePreviewTimestamp,
        onUpdateVideo,
        videoRef,
      ],
    );

    const onPreviewEnd = useCallback(() => {
      setSeekPosition(0);
      onResetPreview();
    }, [onResetPreview]);

    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: <We need to be able to interact with the seekbar, but we don't want to use a button because it would interfer with the children components>
      <div
        className={classNames('relative grow select-none', className)}
        onMouseEnter={onPreviewStart}
        onMouseLeave={onPreviewEnd}
        onMouseMove={onPreviewStart}
        style={{
          WebkitTouchCallout: 'none',
        }}
      >
        {!isAudioClip && (
          <VideoSeekbarPreview ref={videoPreviewRef}>
            {clip.assets.spriteSheet ? (
              <VideoSeekbarPreviewSpritesheet
                aspectRatio={getClipSpritesheetAspectRatio(clip.assets.spriteSheet)}
                columns={clip.assets.spriteSheet.columns}
                frameCount={getClipSpritesheetFrameCount(clip.assets.spriteSheet)}
                position={position}
                rows={clip.assets.spriteSheet.rows}
                src={clip.assets.spriteSheet.src}
              />
            ) : (
              <VideoSeekbarPreviewHls clip={clip} ref={videoRef} />
            )}
          </VideoSeekbarPreview>
        )}
        <input
          className="absolute bottom-0 h-6 w-full cursor-pointer appearance-none bg-transparent [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent [&::-ms-thumb]:appearance-none [&::-webkit-slider-thumb]:appearance-none"
          max={1}
          min={0}
          onChange={() => onSeek(seekPosition)}
          onMouseDown={onSeekStart}
          onMouseUp={() => onSeekEnd(seekPosition)}
          onTouchEnd={() => onSeekEnd(seekPosition)}
          onTouchStart={onSeekStart}
          ref={inputRef}
          step="any"
          type="range"
          value={progressPercentage}
        />
        <VideoSeekbarProgress color="teal" percentage={progressPercentage * 100}>
          {chapters && chapters?.length > 0 && (
            <VideoSeekbarProgressChapters chapters={chapters} duration={duration} isFullscreen={isFullscreen} />
          )}
        </VideoSeekbarProgress>
        <VideoSeekbarProgress
          className="absolute top-1/2 -translate-y-1/2 bg-transparent"
          color="grey"
          percentage={processedPercentage * 100}
        >
          {chapters && chapters?.length > 0 && (
            <VideoSeekbarProgressChapters chapters={chapters} duration={duration} isFullscreen={isFullscreen} />
          )}
        </VideoSeekbarProgress>
        {renderTimestamp?.()}
      </div>
    );
  },
);

VideoSeekbar.displayName = 'VideoSeekbar';
